/**
 * Core-100 owner direction 10 — OptiBay staff review account requests and set whether each account
 * type takes requests at all (closed = invitation only). Every decision is made by the database RPC
 * `decide_account_request`, which requires an internal admin and records who decided, when and why.
 */
import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { DeactivatedAccountsCard } from '@/components/admin/DeactivatedAccountsCard';
import { REQUEST_TYPE_LABEL, type AccountRequestRow, type AccountRequestType } from '@/pages/RequestAccount';

interface AdminRow extends AccountRequestRow { website: string | null; message: string | null; decided_at: string | null }

export function describeDecision(outcome: string | undefined): string {
  switch (outcome) {
    case 'APPROVED': return 'Approved — the account was created.';
    case 'REJECTED': return 'Declined.';
    case 'ALREADY_DECIDED': return 'Someone already decided this request.';
    case 'ACCOUNT_ALREADY_HELD': return 'This person already has a creator account; nothing was created.';
    default: return 'The decision was not recorded.';
  }
}

export default function AccountRequestsAdmin() {
  const [rows, setRows] = useState<AdminRow[]>([]);
  const [open, setOpen] = useState<Record<AccountRequestType, boolean>>({ creator: false, school: false });
  const [reasons, setReasons] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    const [{ data: settings }, { data: requests }] = await Promise.all([
      supabase.from('platform_account_request_settings' as never).select('request_type, requests_open'),
      supabase.from('platform_account_requests' as never)
        .select('id, request_type, organisation_name, website, message, status, decision_reason, created_at, decided_at')
        .order('created_at', { ascending: false }),
    ]);
    const s = { creator: false, school: false } as Record<AccountRequestType, boolean>;
    for (const r of ((settings ?? []) as unknown) as Array<{ request_type: AccountRequestType; requests_open: boolean }>) s[r.request_type] = r.requests_open;
    setOpen(s);
    const list = ((requests ?? []) as unknown) as AdminRow[];
    setRows([...list.filter((r) => r.status === 'pending'), ...list.filter((r) => r.status !== 'pending')]);
  }, []);
  useEffect(() => { void load(); }, [load]);

  const toggle = async (t: AccountRequestType, value: boolean) => {
    const { error } = await supabase.rpc('set_account_requests_open' as never, { p_request_type: t, p_open: value } as never);
    if (error) { toast.error('Not changed'); return; }
    void load();
  };

  const decide = async (id: string, decision: 'approve' | 'reject') => {
    const reason = (reasons[id] ?? '').trim();
    if (decision === 'reject' && !reason) { toast.error('Say why the request is declined'); return; }
    const { data, error } = await supabase.rpc('decide_account_request' as never, { p_request_id: id, p_decision: decision, p_reason: reason || null } as never);
    if (error) { toast.error('The decision was not recorded'); return; }
    toast.success(describeDecision((data as { outcome?: string } | null)?.outcome));
    void load();
  };

  return (
    <div className="container mx-auto max-w-3xl space-y-6 p-4">
      <Card>
        <CardHeader>
          <CardTitle>Account requests</CardTitle>
          <CardDescription>When requests are off, that account type is by invitation only.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {(['creator', 'school'] as const).map((t) => (
            <label key={t} className="flex items-center justify-between rounded-md border p-3">
              <span className="text-sm">{REQUEST_TYPE_LABEL[t]} accounts take requests</span>
              <Switch checked={open[t]} onCheckedChange={(v) => void toggle(t, v)} aria-label={`${REQUEST_TYPE_LABEL[t]} requests open`} />
            </label>
          ))}
        </CardContent>
      </Card>
      {rows.map((r) => (
        <Card key={r.id} data-testid="admin-account-request">
          <CardContent className="space-y-2 pt-6">
            <div className="flex items-center justify-between">
              <p className="font-medium">{REQUEST_TYPE_LABEL[r.request_type]} · {r.organisation_name}</p>
              <Badge variant={r.status === 'pending' ? 'default' : 'outline'}>{r.status}</Badge>
            </div>
            {r.website && <p className="text-xs text-muted-foreground">{r.website}</p>}
            {r.message && <p className="text-sm whitespace-pre-wrap">{r.message}</p>}
            {r.status === 'pending' ? (
              <div className="flex flex-wrap items-center gap-2">
                <Input className="max-w-sm" placeholder="Reason (required to decline)" value={reasons[r.id] ?? ''}
                  onChange={(e) => setReasons((x) => ({ ...x, [r.id]: e.target.value }))} />
                <Button size="sm" onClick={() => void decide(r.id, 'approve')}>Approve</Button>
                <Button size="sm" variant="outline" onClick={() => void decide(r.id, 'reject')}>Decline</Button>
              </div>
            ) : r.decision_reason ? (
              <p className="text-xs text-muted-foreground">Reason: {r.decision_reason}</p>
            ) : null}
          </CardContent>
        </Card>
      ))}
      <DeactivatedAccountsCard />
    </div>
  );
}
